import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import MikaCard from '../../components/MikaCard/MikaCard';
import { getClientInitials, getAvatarColor, getClientStatus, getProjectCount, formatDate, CLIENT_STATUS_I18N_KEY } from './contactHelpers';

interface ContactCardGridProps {
    data: any[];
    onEdit: (client: any) => void;
    // Optional — when undefined the delete button is hidden. Callers gate this by
    // permission (superAdmin OR fullPrivileges) so users without delete rights
    // don't see the affordance at all.
    onDelete?: (client: any) => void;
}

const ContactCardGrid: React.FC<ContactCardGridProps> = ({ data, onEdit, onDelete }) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });

    return (
        <div className="ccg-grid">
            {data.length === 0 ? (
                <div className="ccg-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
                    </svg>
                    <p><FormattedMessage id="contact.list.empty" defaultMessage="No clients found" /></p>
                </div>
            ) : (
                data.map((client) => {
                    const initials = getClientInitials(client);
                    const avatarColor = getAvatarColor(client.firstnameAndLastname || '');
                    const status = getClientStatus(client);
                    const projectCount = getProjectCount(client);
                    const fullName = `${client.firstnameAndLastname || ''} ${client.lastName || ''}`.trim();
                    const location = [client.city, client.country].filter(Boolean).join(', ');
                    const agent = client.user?.userName;

                    return (
                        <MikaCard
                            key={client.id}
                            onClick={() => onEdit(client)}
                            header={
                                <div className="ccg-card-header">
                                    <div className="ccg-avatar" style={{ background: avatarColor }}>
                                        {initials}
                                    </div>
                                    <div className="ccg-card-name">
                                        <span className="ccg-name">{fullName || '\u2014'}</span>
                                        {client.companyName && <span className="ccg-company">{client.companyName}</span>}
                                    </div>
                                    <span className="ccg-status-badge" style={{ color: status.color, background: status.bg }}>
                                        <FormattedMessage id={CLIENT_STATUS_I18N_KEY[status.variant]} defaultMessage={status.variant} />
                                    </span>
                                </div>
                            }
                            body={
                                <div className="ccg-card-body-content">
                                    <div className="ccg-info-grid">
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="contact.list.column.firstName" defaultMessage="First name" /></span>
                                            <span className="ccg-info-value">{client.firstnameAndLastname || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="contact.list.column.lastName" defaultMessage="Last name" /></span>
                                            <span className="ccg-info-value">{client.lastName || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="contact.list.column.phone" defaultMessage="Phone" /></span>
                                            <span className="ccg-info-value">{client.phoneNumber || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="contact.list.column.city" defaultMessage="City" /></span>
                                            <span className="ccg-info-value">{client.city || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="contact.list.column.country" defaultMessage="Country" /></span>
                                            <span className="ccg-info-value">{client.country || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="contact.list.column.projectCount" defaultMessage="Projects" /></span>
                                            <span className="ccg-info-value">
                                                {projectCount > 0
                                                    ? <span className="ccg-project-badge">{projectCount}</span>
                                                    : '0'
                                                }
                                            </span>
                                        </div>
                                        {agent && (
                                            <div className="ccg-info-item ccg-info-full">
                                                <span className="ccg-info-label"><FormattedMessage id="contact.list.column.agent" defaultMessage="Agent" /></span>
                                                <span className="ccg-info-value">{agent}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            }
                            footer={
                                <div className="ccg-card-footer">
                                    <span className="ccg-date">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                                        </svg>
                                        {formatDate(client.createdAt)}
                                    </span>
                                    <div className="ccg-footer-actions">
                                        <button
                                            className="ccg-action-btn"
                                            onClick={(e) => { e.stopPropagation(); onEdit(client); }}
                                            title={fm('contact.list.edit', 'Edit')}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                                            </svg>
                                        </button>
                                        {onDelete && (
                                            <button
                                                className="ccg-action-btn ccg-action-delete"
                                                onClick={(e) => { e.stopPropagation(); onDelete(client); }}
                                                title={fm('contact.list.delete', 'Delete')}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            }
                        />
                    );
                })
            )}
        </div>
    );
};

export default ContactCardGrid;
